import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';

import { env } from '@amfam/environment';
import { DataDogService } from '@amfam/shared/analytics/lib/services/data-dog.service';

import {
  ContactDetails,
  ContactUsSection,
  GetContactDetailsResponse
} from '../models/contact-us.model';

// Shown when the contact-us call fails or returns no sections
const FALLBACK_CONTACT_US_SECTION: ContactUsSection = {
  label: '',
  phoneNumbers: { customerCareNumber: '1-800-692-6326' },
  hoursOfOperation: {}
};

const FALLBACK_CONTACT_DETAILS: ContactDetails = {
  customerId: '',
  customerGroupId: '',
  scenario: 'UNDETERMINED',
  contactUs: [FALLBACK_CONTACT_US_SECTION]
};

@Injectable({
  providedIn: 'root'
})
export class ContactUsService {
  private http = inject(HttpClient);
  private dataDogService = inject(DataDogService);

  // Cold, lazily triggered by the first subscriber; shareReplay(1) means every
  // consumer (desktop popup + mobile header) shares a single HTTP call.
  private contactDetails$: Observable<ContactDetails> = this.http
    .get<GetContactDetailsResponse>(env.userAccountApi + '/contact-us')
    .pipe(
      map(
        ({ response }): ContactDetails => ({
          customerId: response?.customerId ?? '',
          customerGroupId: response?.customerGroupId ?? '',
          scenario: response?.scenario ?? 'UNDETERMINED',
          claimsMessage: response?.claimsMessage,
          contactUs: response?.contactUs?.length
            ? response.contactUs
            : [FALLBACK_CONTACT_US_SECTION]
        })
      ),
      catchError(err => {
        this.dataDogService.log('Failed to load contact details, using fallback', 'error', {
          error: err?.message || err
        });
        if (env.environment !== 'prod') {
          console.error('[ContactUsService] Failed to load contact details, using fallback', err);
        }
        return of(FALLBACK_CONTACT_DETAILS);
      }),
      shareReplay(1)
    );

  getContactDetails(): Observable<ContactDetails> {
    return this.contactDetails$;
  }
}
